import { useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { TbChevronUp, TbVolume, TbWifi } from "react-icons/tb";
import "./trayScene.scss";

// Same five states, colours and English tooltip wording as agent/src/tray.ts (STATE_LABEL / STATE_COLOR) —
// staff hover the real icon and read exactly these words, so the legend quotes them verbatim.
const STATES = ["running", "enrolling", "renewSoon", "revoked", "attention"] as const;
type TrayState = (typeof STATES)[number];

/** Where the desktop app lives once it runs: a drawing of the Windows taskbar corner with the ^ overflow
 *  opened and the Medinvoice icon inside it. The legend is a radio group — picking a status repaints the
 *  icon's dot and tooltip, so staff learn the colours by seeing them, not by reading a table. The drawing is
 *  decorative (aria-hidden); every fact it shows is also in the legend text. */
export function TrayScene() {
    const { t } = useTranslation();
    const [state, setState] = useState<TrayState>("running");

    const onKey = (e: KeyboardEvent<HTMLButtonElement>) => {
        const i = STATES.indexOf(state);
        const next = e.key === "ArrowDown" || e.key === "ArrowRight" ? STATES[(i + 1) % STATES.length]
            : e.key === "ArrowUp" || e.key === "ArrowLeft" ? STATES[(i - 1 + STATES.length) % STATES.length]
                : e.key === "Home" ? STATES[0]
                    : e.key === "End" ? STATES[STATES.length - 1]
                        : null;
        if (!next) return;
        e.preventDefault();
        setState(next);
        document.getElementById(`tray-state-${next}`)?.focus();
    };

    return (
        <figure className="tray-scene">
            <div className="tray-scene__stage" aria-hidden="true">
                <div className="tray-scene__flyout">
                    <span className="tray-scene__slot" />
                    <span className="tray-scene__slot" />
                    <span className="tray-scene__slot" />
                    <span className={`tray-scene__app tray-scene__app--${state}`}>
                        <img src="/media/sync/agent-icon.png" alt="" />
                        <span className="tray-scene__dot" />
                    </span>
                </div>
                <span className="tray-scene__tooltip">Medinvoice Desktop-App — {t(`syncGuide.tray.states.${state}.label`)}</span>
                <div className="tray-scene__bar">
                    <span className="tray-scene__chevron"><TbChevronUp /></span>
                    <TbWifi />
                    <TbVolume />
                    <span className="tray-scene__clock">09:41</span>
                </div>
            </div>

            <figcaption className="tray-scene__caption">{t("syncGuide.tray.caption")}</figcaption>

            <div className="tray-scene__legend" role="radiogroup" aria-label={t("syncGuide.tray.legendLabel")}>
                {STATES.map((s) => {
                    const selected = s === state;
                    return (
                        <button
                            key={s}
                            type="button"
                            role="radio"
                            id={`tray-state-${s}`}
                            aria-checked={selected}
                            tabIndex={selected ? 0 : -1}
                            className={`tray-scene__state tray-scene__state--${s}${selected ? " is-selected" : ""}`}
                            onClick={() => setState(s)}
                            onKeyDown={onKey}
                        >
                            <span className="tray-scene__state-dot" aria-hidden="true" />
                            <span className="tray-scene__state-text">
                                <span className="tray-scene__state-meaning">{t(`syncGuide.tray.states.${s}.meaning`)}</span>
                                <span className="tray-scene__state-todo">{t(`syncGuide.tray.states.${s}.todo`)}</span>
                            </span>
                        </button>
                    );
                })}
            </div>

            <p className="tray-scene__menu">{t("syncGuide.tray.menu")}</p>
        </figure>
    );
}
